import { CellSource, Grid, GridEvent, GridSource, OptionSource, ElementSource, isFormula, ConditionalFormatSource } from "@omegagrid/grid";
import { TreeAdapter, TreeAdapterParams, TreeSource } from "@omegagrid/tree";
import { CustomAction, CustomParams, ExportType, ExtensionOptions, QueryResult } from "../../types";
import { ExtensionController } from "./extensionController";
import { ColorTypes, dom, getThemeManager, utils } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { Query } from "@reporting/rp-types/ext";
import { GridNormalizer } from "./gridNormalizer";
import rp from "@reporting/rp-app-host/src/global";
// side-effect import: GridEditor is referenced only in type positions, but the
// rp-editor-grid custom element must register on the standalone page too
import "@reporting/rp-app-host/src/editors/gridEditor";
import { GridEditor } from "@reporting/rp-app-host/src/editors/gridEditor";
import { ExportType as GridExportType, GridEditorAdapter } from "@reporting/rp-app-host/src/editors/gridEditorAdapter";
import { SqlEditorResult } from "@reporting/rp-app-host/src/editors/sqlEditorResult";
import { readResultStream, SqlDataset, SqlExecCallbackArgs } from "@reporting/rp-app-host/src/client/sqlStream";
import type { SqlError } from "@reporting/rp-app-host/src/types";
import { parseSqlTableType } from "../../utils";
import { ExtensionEvent } from "../engine";
import { CsvArrayStream } from '@reporting/rp-utils-csv';
import { render, html } from "lit";
import qs from 'qs';

/** the extension grid keeps the formulas the GridEditor turns off: the definition query may deliver FORMULA cells */
const DEFAULT_GRID_OPTIONS: OptionSource = {
	hideOuterBorder: true,
	formulas: true,
};

type ResultType = 'columns' | 'rows' | 'cells' | 'rules' | 'styles' | 'options';

const convertState = (state: string) => {
	switch (state) {
		case 'new': return 'I';
		case 'edited': return 'U';
		case 'deleted': return 'D';
		default: return null;
	}
}

/**
 * A save query may end with `select 0|1 as RELOAD` (legacy spreadsheet contract): a control
 * result, not shown to the user. Returns undefined for any other dataset, null for a NULL value.
 */
export const getReloadFlag = (dataset: SqlDataset): boolean | null | undefined => {
	if (dataset.columns.length != 1 || dataset.columns[0].name?.toUpperCase() != 'RELOAD' || dataset.data.length > 1) return undefined;
	const value = dataset.data[0]?.[0];
	if (value == null) return null;
	return value === '1' || value.toLowerCase() === 'true';
}

const createDataIndex =(columns: {name: string, type: 'string' | 'number' | 'boolean'}[]) => {
	const index: {
		r: number,
		row: number,
		c: number,
		col: number,
		s: number,
		status: number,
		v: number,
		value: number,
		data: [number, string][],
		props: [number, string][]
	} = {
		r: null,
		row: null,
		c: null,
		col: null,
		v: null,
		value: null,
		s: null,
		status: null,
		data: [],
		props: [],
	};

	columns.forEach((c, i) => {
		if (c.name == 'r') index.r = i;
		else if (c.name == 'row') index.row = i;
		else if (c.name == 'c') index.c = i;
		else if (c.name == 'col') index.col = i;
		else if (c.name == 'v') index.v = i;
		else if (c.name == 'value') index.value = i;
		else if (c.name == 's') index.s = i;
		else if (c.name == 'status') index.status = i;
		else if (c.name.startsWith('prop_')) index.props.push([i, c.name.substring(5)]);
		else index.data.push([i, c.name]);
	});

	return index;
};

export class GridExtensionAdapter extends GridEditorAdapter {

	constructor(private source: GridSource) {
		super();
	}

	getColumns() {
		return this.source.columns || [];
	}

	getRows() {
		return this.source.rows || [];
	}

	getCells() {
		return this.source.cells || [];
	}

	getRules() {
		return this.source.rules || [];
	}

	getOptions() {
		return this.createOptions({pageSize:0, ...this.source.options});
	}
}

export class GridController extends ExtensionController {

	constructor(engine: ExtensionController['engine']) {
		super(engine);
		// the standalone page never touches the og theme on its own; the grid needs
		// the --og-* variables, so the .sheet path activates it explicitly (the first
		// getThemeManager call activates the os-matching theme)
		getThemeManager();
	}

	private _gridSource: GridSource;
	get gridSource() { return this._gridSource; }

	private _grid: Grid;
	get grid() { return this._grid || this._editor?.grid }

	private _editor: GridEditor;
	get editor() { return this._editor; }

	get queries() { return this.engine.getResourceQueries(this.engine.mainResourceId); }

	private _error: string;
	get error() { return this._error; }

	get definitionQuery() : Query {
		return this.queries.find(q => q.type == 'definition');
	}

	get saveQuery() : Query {
		return this.queries.find(q => q.type == 'save');
	}

	get saveDataParams() {
		const query = this.saveQuery;
		if (!query) return null;
		return this.engine.extension.parameters?.filter(p => p.id_thread === query.id_thread) ?? [];
	}

	get isSavable() { return !!this.saveQuery }

	getCustomActions(): CustomAction[] {
		const actions: CustomAction[] = [];
		if (this.queries.some(q => q.type == 'save')) {
			actions.push({
				id: 'save',
				icon: 'save',
				text: msg('Save changes'),
				color: ColorTypes.Green,
			});

			actions.push({
				id: 'discard',
				icon: 'trash',
				text: msg('Discard changes'),
				color: ColorTypes.Red,
			});
		}
		return actions;
	}

	getExportTypes(): ExportType[] {
		return [
			{type: 'xlsx'},
			// {type: 'csv'},
			// {type: 'pdf'},
		];
	}

	getResultIndices(result: QueryResult) : Partial<Record<ResultType, number>> {
		if (result?.[0]?._index == null) {
			return {columns: 0, rows: 1, cells: 2}
		}

		const indices: Partial<Record<ResultType, number>> = {};
		for (let i = 0; i < result.length; i++) {
			indices[result[i]._type as ResultType] = result[i]._index as number;
		}

		return indices;
	}

	private _treeAdapter: TreeAdapter;
	private _treeSources: Map<string, TreeSource>;
	get treeSources() {
		return this._treeSources ?? (this._treeSources = new Map());
	}

	get treeAdapter(): TreeAdapter {
		const _this = this;
		return this._treeAdapter ?? (this._treeAdapter = new class extends TreeAdapter {
			// one adapter serves all dropdown cells, the dropdown would otherwise keep the tree of the first opened one (#333)
			getOptions() {
				return {alwaysRefresh: true};
			}

			async getTree(params?: TreeAdapterParams): Promise<TreeSource> {
				const cell = _this.grid.selecting.activeCell;
				if (!cell) return [];
				let ddq = (cell.getProperty('ddq') ?? cell.column.getProperty('ddq') ?? cell.row.getProperty('ddq')) as string;
				if (isFormula(ddq)) {
					ddq = _this.grid.model.calculations.evaluateExpression(ddq, cell) as string;
				}

				if (!_this.treeSources.has(ddq)) {
					const source: TreeSource = [];
					const parts = ddq.split('?', 2);
					const queryParams: CustomParams = parts.length > 1 ? qs.parse(parts[1]) as CustomParams : {};
					const queryId = _this.engine.getQueryId(parts[0]);
					if (queryId) {
						const res = await _this.engine.dataRelay.exec(queryId, null, queryParams);
						if (res.length) {
							res[0].forEach(row => source.push({
								k: (row.key ?? row.k) as string,
								v: (row.value ?? row.v) as string,
							}));
						}
					} else {
						const error = `invalid query "${ddq}"`;
						source.push({k: null, v: error});
						rp.log.error(error);
					}
					_this.treeSources.set(ddq, source);
				}

				return _this.treeSources.get(ddq);
			}
		});
	}

	private initGridElement(elm: ElementSource, columns?: (string | number)[] | null) {
		elm = GridNormalizer.normalizeDataItem(elm, columns);
		if (elm.t == 's') elm.adapter = this.treeAdapter;
		return elm;
	}

	async getSourceData(template: string): Promise<GridSource> {
		this._error = null;
		let source: GridSource;
		try {
			source = JSON.parse(template) as GridSource;
		} catch (error) {
			console.error('Failed to create GridSource from template ', error);
			source = {};
		}

		const query = this.definitionQuery;
		if (query) {
			try	{
				const result = await this.engine.dataRelay.execStream(
					query.id_thread,
					this.engine.mainResourceId,
					this.engine.parameters
				);

				const resultIndices = this.getResultIndices(result[0]);
				for (const [key, index] of Object.entries(resultIndices)) {
					if (index == null) continue;
					const res = result[index];
					if (!res) continue;
					source[key as keyof GridSource] = res;
				}
			} catch (error) {
				if ((error as Error).message) {
					this._error = (error as Error).message;
				} else {
					this._error = String(error);
				}
			}
		}

		let columnData: (string|number)[];
		if (source.columns) {
			source.columns = source.columns.map(col => this.initGridElement(col));
			columnData = source.columns.map(col => col.data);
			source.columns.forEach((c, i) => c.data = i);
		}
		if (source.rows) source.rows = source.rows.map(row => this.initGridElement(row, columnData));
		if (source.cells) source.cells = source.cells.map(cell => this.initGridElement(cell) as CellSource);
		if (source.rules) source.rules = source.rules.map(rule => GridNormalizer.normalizeRuleItem(rule)) as ConditionalFormatSource;
		if (source.columns) source.columns = GridNormalizer.normalizeColumns(source.columns);

		source.options = utils.mergeDeep({}, DEFAULT_GRID_OPTIONS, source.options || {});
		return source;
	}

	// _onGridAction = (e: GridEvent) => {}

	_onGridClick = (e: GridEvent) => {
		if (!e.cell) return;

		const properties = {...e.cell.column.properties, ...e.cell.row.properties, ...e.cell.properties};
		const detail = properties.detail as string;
		if (!detail) return; // *.sheet|*.html

		const resource = this.engine.extension.resources.find(r => r.name == detail);
		if (!resource) {
			rp.host.showAlert({type: 'danger', content: `Detail resource not found: ${detail}`});
			return;
		}

		if (!this.engine.context) {
			console.warn(`Extension detail '${detail}' is only available in analytics context`);
			return;
		}

		const {detail: _, ...parameters} = properties;
		this.engine.dispatchEvent(new ExtensionEvent('detail', {
			engine: this.engine,
			data: {
				idResource: resource.id_custom_resource,
				caption: resource.name,
				parameters: parameters as CustomParams
			}
		}));
	}

	async render(target: HTMLElement, template: string, data?: CustomParams): Promise<void> {
		this._grid = null;
		this._editor = null;
		this._gridSource = await this.getSourceData(template);

		if (this._error) {
			render(html`<og-alert .text="${this._error}" type="danger" style="margin: 10px"></og-alert>`, target);
			return;
		}

		const attributes: Record<string, any> = {
			adapter: new GridExtensionAdapter(this._gridSource),
			style: {width: '100%', height: '100%'},
			listeners: [
				// ['grid.action', this._onGridAction],
				['grid.click', this._onGridClick]
			]
		};

		if (this.isSavable) {
			this._editor = dom.createElement<GridEditor>('rp-editor-grid', {noToolbar: true, ...attributes});
			target.appendChild(this._editor);
		} else {
			this._grid = dom.createElement<Grid>('og-grid', attributes);
			target.appendChild(this._grid);
		}
	}

	getCellData(columns: { name: string, type: 'string' | 'number' | 'boolean' }[]) {
		const all = (this._gridSource?.options as any)?.saveMode == 'all'
		const index = createDataIndex(columns);
		return (this._editor?.getCellData(all) ?? []).map(cell => {
			const row = new Array(columns.length);
			if (index.r != null) row[index.r] = cell.r;
			if (index.row != null) row[index.row] = cell.r;
			if (index.c != null) row[index.c] = cell.c;
			if (index.col != null) row[index.col] = cell.c;
			if (index.v != null) row[index.v] = cell.v;
			if (index.value != null) row[index.value] = cell.v;
			if (index.s != null) row[index.s] = convertState(cell.state);
			if (index.status != null) row[index.status] = convertState(cell.state);
			index.props.forEach(i => row[i[0]] = cell.props[i[1]]);
			return row;
		});
	}

	getRowData(columns: {name: string, type: 'string' | 'number' | 'boolean'}[]) {
		const all = (this._gridSource?.options as any)?.saveMode == 'all';
		const index = createDataIndex(columns);

		return (this._editor?.getRowData(all) ?? []).map((r, i) => {
			const row = new Array(columns.length);
			if (index.r != null) row[index.r] = i;
			if (index.row != null) row[index.row] = i;
			if (index.status != null) row[index.status] = convertState(r.state);
			index.data.forEach(i => row[i[0]] = r.data[i[1]]);
			index.props.forEach(i => row[i[0]] = r.props[i[1]]);
			return row;
		});
	}

	async save() {
		if (!this.isSavable) return;
		this._editor.showLoader();
		const saveQuery = this.saveQuery;
		if (!saveQuery) return;
		const params = this.saveDataParams;
		const data: CustomParams = {};
		params.forEach(p => {
			if (p.name.toLowerCase() == 'data') data['data'] = this.getCellData(parseSqlTableType(p.data_type || ''));
			else if (p.name.toLowerCase() == 'rows') data['rows'] = this.getRowData(parseSqlTableType(p.data_type || ''));
		});

		let response: Response;
		try {
			response = await rp.ms('extensions').postRaw(`exec/${saveQuery.id_thread}`, data, {
				csvParams: {
					quote: '"',
					delimiter: '\t',
					header: true,
					headerJson: true,
					nullValue: 'NULL'
				},
			}, {headers: {accept: 'text/csv'}});
		} catch (error) {
			// if (cb) cb({message: {index: 0, errors: [{error: error}]}});
			rp.host.showAlert({type: 'danger', content: error.message || 'Error saving data'});
		}

		let reload: boolean = null;
		if (response) {
			// collected first and shown in stream order: readResultStream does not await
			// its callback, so adding while the dialog opens would reorder the results
			const items: SqlExecCallbackArgs[] = [];
			const reader = response.body.pipeThrough(new CsvArrayStream({quote: '"', delimiter: '\t', nullValue: 'NULL'})).getReader();
			await readResultStream(reader, (args) => {
				if (args.dataset && items.some(item => item.dataset)) {
					const flag = getReloadFlag(args.dataset);
					if (flag !== undefined) {
						reload = flag;
						return;
					}
				}
				if (args.dataset || args.message) items.push(args);
			});

			if (items.length) {
				const resultComponent = (await rp.host.openDialog({
					id: 'extensions-resultGrid',
					closable: true,
					height: 400,
					width: 600,
					caption: 'Save result',
					location: 'center'
				})).component as unknown as SqlEditorResult;

				await new Promise<void>(resolve => setTimeout(resolve, 500));
				items.forEach(args => {
					if (args.dataset) resultComponent.addDataset(args.dataset);
					if (args.message) resultComponent.addMessage(args.message);
				});
			}
		}

		this._editor.clearUpdates(false);
		this._editor.hideLoader();

		// a RELOAD result of the save query overrides the reloadAfterSave grid option
		if (response && (reload ?? !!(this._gridSource?.options as {reloadAfterSave?: boolean})?.reloadAfterSave)) {
			await this.engine.refresh();
		}
	}

	discard() {
		this._editor?.discard();
	}

	onAction(id: string) {
		switch (id) {
			case 'save':
				this.save();
				break;
			case 'discard':
				this.discard();
				break;
		}
	}

	async export(type: ExportType) {
		if (this._editor) {
			// editor wraps the export with its loader
			await this._editor.export(type.type as GridExportType);
		} else if (this._grid) {
			await (this._grid.adapter as GridEditorAdapter).export(type.type as GridExportType);
		}
	}

	getOptions() : ExtensionOptions {
		return this.initOptions({});
	}

}
